import React, { RefObject, createRef } from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import {
  API,
  productName,
  AuditProductAPI,
  AuditBatchAPI,
  IBaseProduct,
  IBatchResponse,
  EditAuditBatch,
} from '@getgreenline/homi-shared'
import { Button, message, Spin, Icon } from 'antd'
import { AuditStore } from '../EditAuditStore'
import { RefreshSnapshotButton } from './RefreshSnapshotButton'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import './Main.scss'
import { BarcodeListener } from '../../../../../../components/BarcodeListener'
import { IProductWithBatchTrackStatus } from '../'
import { EditAuditTableV1 } from './EditAuditTableV1'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'

interface Props {
  audit: AuditModels.CreateInventoryAudit
  auditStore: AuditStore
  createAuditProducts: (
    productsToAdd: IProductWithBatchTrackStatus[],
  ) => Promise<AuditProductModels.EditAuditProduct[]>
  isBarcodeListening: boolean
  setRefMainElement: (refMainElement: RefObject<HTMLDivElement>) => void
  focusOnAuditProductRecord: (matchingProductRecord: AuditProductModels.EditAuditProduct) => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  areDeletingAllRecords: boolean
}

@inject('currentCompanyStore')
@observer
export class Main extends React.Component<Props & InjectedProps, State> {
  company = this.props.currentCompanyStore!.company!

  refMainElement: RefObject<HTMLDivElement> = createRef()

  constructor(props: Props) {
    super(props)

    this.state = {
      areDeletingAllRecords: false,
    }
  }

  componentDidMount = () => {
    this.props.setRefMainElement(this.refMainElement)
  }

  checkBarcode = async (barcode: string, lot?: string) => {
    try {
      const matchingBaseProduct = await this.getMatchingBaseProduct(barcode)

      if (!matchingBaseProduct) {
        return
      }

      const auditProductRecord = await this.getAuditProductRecord(matchingBaseProduct)

      if (auditProductRecord.isBatchTracked && lot) {
        await this.updateBatchOnScan(auditProductRecord, lot)
      }

      const updatedProductRecord = await this.updateAuditProductRecord(auditProductRecord)

      // Display the message after product is scanned
      this.printScanMessage(undefined, updatedProductRecord, lot)

      // Focus the product row when it's scanned
      this.props.focusOnAuditProductRecord(updatedProductRecord)
    } catch (error) {
      message.error({
        content: parseErrorMsg(error) || 'There was an error while updating the audit product',
        duration: 15,
      })
    }
  }

  getMatchingBaseProduct = async (barcode: string): Promise<IBaseProduct | undefined> => {
    const flatProducts = await API.getFlatProducts(this.company.id, { barcode })

    // Display the error message if product doesn't exist in the sytem
    if (flatProducts.length <= 0) {
      this.printScanMessage(barcode)
      return
    }

    return flatProducts[0]
  }

  getAuditProductRecord = async (
    matchingBaseProduct: IBaseProduct,
  ): Promise<AuditProductModels.EditAuditProduct> => {
    const matchingAuditProductRecord = this.props.audit.getAuditProduct(matchingBaseProduct.id)

    if (matchingAuditProductRecord) {
      return matchingAuditProductRecord
    } else {
      const auditProductRecords = await this.props.createAuditProducts([
        {
          sku: matchingBaseProduct.sku,
          barcode: matchingBaseProduct.barcode,
          productId: matchingBaseProduct.id,
          productName: productName(matchingBaseProduct),
          productUnit: matchingBaseProduct.unit,
          isBatchTracked: matchingBaseProduct.isBatchTracked,
          purchasePrice: matchingBaseProduct.purchasePrice,
        },
      ])

      return auditProductRecords[0]
    }
  }

  updateAuditProductRecord = async (
    auditProductRecord: AuditProductModels.EditAuditProduct,
  ): Promise<AuditProductModels.EditAuditProduct> => {
    const { notes, ...updatedAuditProduct } = await AuditProductAPI.patchAuditProduct(
      this.company.id,
      {
        id: auditProductRecord.id,
        inventoryAuditId: this.props.audit.id,
        productId: auditProductRecord.productId,
        expectedQuantity: auditProductRecord.expectedQuantity,
        enteredQuantity: (auditProductRecord.enteredQuantity || 0) + 1,
      },
    )

    auditProductRecord.locallySaveEnteredQuantity(updatedAuditProduct.enteredQuantity)
    auditProductRecord.setEnteredQuantity(updatedAuditProduct.enteredQuantity)

    this.props.auditStore.setAuditProductEQSyncCache({
      syncAttempted: true,
      syncSuccessful: true,
      cachedAuditProductContract: updatedAuditProduct,
    })

    return auditProductRecord
  }

  updateBatchOnScan = async (product: AuditProductModels.EditAuditProduct, batchNumber: string) => {
    const { productId } = product

    // If available batches of the product doesn't exist, set product with available batches
    const availableBatchOptions = this.props.auditStore.getAllBatchOptionsForProduct(productId)

    if (!availableBatchOptions) {
      await this.props.auditStore.setAvailableBatchOptions(product.productId)
    }

    // If scanned batch number doesn't exist in the database, create a new batch option
    const matchingBatchOption = await this.getMatchingBatchOption(product, batchNumber)

    const targetAuditBatchRecord = await this.getAuditBatchRecord(
      product,
      batchNumber,
      matchingBatchOption,
    )

    // Increase batch quantity
    const updatedAuditBatch = await AuditBatchAPI.editAuditBatch(
      this.props.audit.companyId,
      this.props.audit.id,
      {
        id: targetAuditBatchRecord.id,
        inventoryAuditProductId: product.id,
        batchId: targetAuditBatchRecord.batchId,
        expectedQuantity: targetAuditBatchRecord.expectedQuantity,
        enteredQuantity: (targetAuditBatchRecord.enteredQuantity || 0) + 1,
      },
    )

    targetAuditBatchRecord.locallySaveEnteredQuantity(updatedAuditBatch.enteredQuantity)
    targetAuditBatchRecord.setEnteredQuantity(updatedAuditBatch.enteredQuantity)
    this.props.auditStore.setAuditBatchSyncCache({
      syncAttempted: true,
      syncSuccessful: true,
      cachedAuditBatchContract: updatedAuditBatch,
    })
  }

  getMatchingBatchOption = async (
    product: AuditProductModels.EditAuditProduct,
    batchNumber: string,
  ): Promise<IBatchResponse> => {
    const trimmedNewBatchName = batchNumber.trim()

    const matchingBatchOption = this.props.auditStore.getMatchingBatchOption(
      product.productId,
      trimmedNewBatchName,
    )

    if (matchingBatchOption) {
      return matchingBatchOption
    } else {
      return this.props.auditStore.createNewBatchOption(product, batchNumber)
    }
  }

  getAuditBatchRecord = async (
    product: AuditProductModels.EditAuditProduct,
    batchNumber: string,
    batchOption: IBatchResponse,
  ): Promise<EditAuditBatch> => {
    const targetAuditBatchRecord = product.batches.find(
      (batch) => batch.batchNumber === batchNumber,
    )

    if (targetAuditBatchRecord) {
      return targetAuditBatchRecord
    }

    const addedAuditBatch = await AuditBatchAPI.addAuditBatch(
      this.props.audit.companyId,
      this.props.audit.id,
      {
        inventoryAuditProductId: product.id,
        batchId: batchOption.id,
        expectedQuantity: batchOption.quantityRemaining,
        enteredQuantity: null,
      },
    )

    return product.addBatch({
      id: addedAuditBatch.id!,
      inventoryAuditProductId: addedAuditBatch.inventoryAuditProductId,
      batchId: addedAuditBatch.batchId,
      batchNumber: batchOption.batchNumber,
      quantity: 0,
      quantityRemaining: addedAuditBatch.expectedQuantity,
    })
  }

  printScanMessage = (
    barcode?: string,
    matchingProductRow?: AuditProductModels.EditAuditProduct,
    lot?: string,
  ) => {
    const messageKey = `${barcode}_${lot}`

    message.loading({
      content: (
        <>
          Processing Barcode <strong>{barcode}</strong>...
        </>
      ),
      key: `${barcode}_${lot}`,
    })

    if (!matchingProductRow) {
      return message.error({
        content: (
          <>
            Barcode <strong>{barcode}</strong> not found
          </>
        ),
        key: messageKey,
      })
    }

    const { productName, isBatchTracked } = matchingProductRow

    if (!isBatchTracked) {
      return message.success({
        content: (
          <>
            <strong>{productName}</strong> scanned
          </>
        ),
        key: messageKey,
      })
    }

    if (lot) {
      return message.success({
        content: (
          <>
            [Lot tracked] <strong>{productName}</strong> scanned and lot number{' '}
            <strong>{lot}</strong> scanned
          </>
        ),
        key: messageKey,
        duration: 8,
      })
    } else {
      return message.warn({
        content: (
          <>
            [Lot tracked] <strong>{productName}</strong> scanned, but{' '}
            <strong>lot number not scanned</strong>
          </>
        ),
        key: messageKey,
        duration: 8,
      })
    }
  }

  deleteAllProductsAndBatches = async () => {
    if (!window.confirm('Are you sure?')) {
      return
    }

    this.setState({ areDeletingAllRecords: true })

    try {
      await AuditBatchAPI.deleteAllAuditBatches(this.company.id, this.props.audit.id)

      await AuditProductAPI.deleteAllAuditProducts(this.company.id, this.props.audit.id)

      this.props.audit.removeAllAuditProducts()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while clearing all rows')
    } finally {
      this.setState({ areDeletingAllRecords: false })
    }
  }

  render() {
    const { auditStore, audit, currentCompanyStore, isBarcodeListening } = this.props

    return (
      <div className='audit-table-section white-rounded-container' ref={this.refMainElement}>
        {auditStore.isEditable && (
          <div id='edit-audit-snapshot' className='audit-table-snapshot-section'>
            {/* id is assigned to auto-focus when the item is scanned */}
            <div className='snapshot-container'>
              <RefreshSnapshotButton auditStore={auditStore} audit={audit} />

              <div>From: {moment(audit.updateDate).format('MMM D, YYYY h:mm a')}</div>
            </div>

            <div>
              <Button
                className='ml-2 btn-clear-all'
                disabled={audit.products.length === 0}
                onClick={this.deleteAllProductsAndBatches}
              >
                Clear all
              </Button>
            </div>
          </div>
        )}

        {this.state.areDeletingAllRecords ? (
          <Spin indicator={<Icon type='loading' spin />} size='large' />
        ) : (
          <EditAuditTableV1
            audit={audit}
            company={currentCompanyStore!.company!}
            editable={auditStore.isEditable}
            auditStore={auditStore}
          />
        )}

        {isBarcodeListening && (
          <BarcodeListener onBarcodeEnter={(barcode, lot) => this.checkBarcode(barcode, lot)} />
        )}
      </div>
    )
  }
}
